import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { SortingState } from '@tanstack/react-table';

export interface DataTableUrlOptions {
  /**
   * Maps DataTable column IDs to backend sort field names.
   * e.g. { created: 'created_at', name: 'name' }
   * If a column ID is not in this map, it is used as-is.
   */
  columnToSortField?: Record<string, string>;
  /**
   * Default number of items per page.
   */
  defaultLimit?: number;
}

export interface DataTableUrlResult {
  /** Current search string from URL */
  search: string;
  /** Current 1-based page number from URL */
  page: number;
  /** Items per page */
  limit: number;
  /** Backend sort field name (e.g. 'created_at'), or undefined if no sort */
  sortBy: string | undefined;
  /** Sort direction, or undefined if no sort */
  sortOrder: 'asc' | 'desc' | undefined;
  /** SortingState for DataTable's `sorting` prop */
  sorting: SortingState;
  /** Handler for DataTable's `onSortingChange` prop — updates URL */
  onSortingChange: (sorting: SortingState) => void;
  /** Handler for search input changes — updates URL and resets page */
  onSearchChange: (value: string) => void;
  /** Handler for page changes — updates URL */
  onPageChange: (page: number) => void;
  /**
   * Call once the list has loaded, with how many pages it has. If the URL points
   * past the end (a stale link, or the last row on the last page removed), it is
   * rewritten to the last page — replacing the history entry, not adding one.
   * Don't call it while loading: an empty list would read as one page.
   */
  clampPage: (pageCount: number) => void;
  /**
   * Write other URL params — a page's own filters — beside sort/search/page.
   * `null`, `undefined` and `''` all remove a param: a filter form's Clear hands
   * back an empty object, a Select's "all" is `''`, and neither must land in the
   * address bar (as the word "undefined", or as a bare `?status=`). A real
   * change returns to page 1; re-applying the values already there writes nothing.
   */
  setParams: (updates: Record<string, string | null | undefined>) => void;
}

/**
 * Hook that syncs DataTable sorting, search, and pagination state with URL search params.
 *
 * URL params used:
 *  - `sortBy`    — backend sort field name
 *  - `sortOrder` — 'asc' | 'desc'
 *  - `search`    — search query
 *  - `page`      — 1-based page number
 *
 * One writing instance per route. The queue of in-flight writes (`pending`,
 * below) is per instance, but the URL it composes on is per page, so two
 * instances mounted on the same route cannot see each other's writes: a second
 * one writing in the same tick builds on the committed URL and silently drops
 * the first's not-yet-committed param. A parent that only *reads* (sorting for
 * a table it renders itself) beside a child that writes is fine; two writers
 * on one route are not.
 */
// One shared object, not a fresh `{}` per call, so the inverted map below is
// built once rather than every render. (`sorting` itself no longer follows the
// map's identity — see where it is derived.)
const NO_COLUMN_MAP: Record<string, string> = {};

type WriteKind = 'search' | 'other';
interface PendingWrite {
  url: string;
  kind: WriteKind;
}

// The page the URL names, clamped: `page` is whatever is in the address bar — a
// hand-edited value, a stale bookmark from before a page adopted this hook
// (those wrote 0-based pages), or junk. Callers document this as 1-based and
// convert with `page - 1`, so a 0 or a NaN leaking through becomes an
// out-of-range request the backend rejects with a 400. The one reader for the
// param, so `?page=0`, `?page=abc` and `?page=1` are the same page everywhere.
function readPage(params: URLSearchParams): number {
  const parsed = parseInt(params.get('page') || '1', 10);
  return Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
}

export function useDataTableUrl(
  options: DataTableUrlOptions = {},
): DataTableUrlResult {
  const { columnToSortField = NO_COLUMN_MAP, defaultLimit = 20 } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const { key: locationKey } = useLocation();

  // The handlers read the URL through these refs rather than closing over it.
  // SearchInput's debounce calls the handler it was given at the last keystroke;
  // a closed-over copy would compare against, and build on, the URL as it was
  // then — after a Back or a Clear all in between, that is the wrong URL. It
  // also keeps the handlers' identities stable across URL changes.
  //
  // `latest` is written after commit, not during render: React Router runs
  // navigations as transitions, and a render React throws away must not leave
  // its URL here. That leaves a gap between a write and its commit, so `pending`
  // holds our own writes, oldest first, until they commit — without it, a
  // second write in that gap (Clear right after a debounced search) would
  // compare against, and build on, the URL from before the first.
  //
  // Each write also records what made it. A search edit replaces the history
  // entry it sits on rather than adding one, and it may only do that to an
  // entry a search write produced: `committedKind` is what produced the entry
  // now committed — one of our search writes, or anything else (another of our
  // writes, a Back, a link).
  const latest = useRef({ searchParams, setSearchParams, columnToSortField });
  const pending = useRef<PendingWrite[]>([]);
  const committedKind = useRef<WriteKind>('other');
  const committedKey = useRef(locationKey);
  useLayoutEffect(() => {
    latest.current = { searchParams, setSearchParams, columnToSortField };
    if (committedKey.current === locationKey) return;
    committedKey.current = locationKey;
    // A navigation committed. If it is one of our writes, that write and any
    // older ones have landed (React may commit several as one); newer ones are
    // still in flight. If it is none of them — a Back, a link — it overrides
    // them all, and the committed URL is the truth again.
    const url = searchParams.toString();
    let landed = -1;
    for (let i = pending.current.length - 1; i >= 0; i--) {
      if (pending.current[i].url === url) {
        landed = i;
        break;
      }
    }
    committedKind.current = landed === -1 ? 'other' : pending.current[landed].kind;
    pending.current = landed === -1 ? [] : pending.current.slice(landed + 1);
  });

  // The URL as the handlers should see it: our newest pending write, else the committed one.
  const current = useCallback(() => {
    const newest = pending.current[pending.current.length - 1];
    return newest !== undefined ? new URLSearchParams(newest.url) : latest.current.searchParams;
  }, []);

  // What produced the entry the handlers are building on.
  const currentKind = useCallback((): WriteKind => {
    const newest = pending.current[pending.current.length - 1];
    return newest !== undefined ? newest.kind : committedKind.current;
  }, []);

  // Apply `edit` to the current URL and write the result — unless nothing changed,
  // which would only add a history entry.
  const write = useCallback(
    (edit: (next: URLSearchParams) => void, { replace = false, kind = 'other' as WriteKind } = {}) => {
      const base = current();
      const next = new URLSearchParams(base);
      edit(next);
      if (next.toString() === base.toString()) return;
      pending.current.push({ url: next.toString(), kind });
      latest.current.setSearchParams(next, { replace });
    },
    [current],
  );

  // Invert the mapping: backend field → column ID
  // A Map, because it is looked up with `sortBy` from the address bar: on a plain
  // object, `?sortBy=constructor` finds Object.prototype's own members and hands
  // the table a function as the column id.
  const sortFieldToColumn = useMemo(() => {
    const map = new Map<string, string>();
    for (const [colId, field] of Object.entries(columnToSortField)) {
      map.set(field, colId);
    }
    return map;
  }, [columnToSortField]);

  // Read state from URL
  const search = searchParams.get('search') || '';
  const page = readPage(searchParams);
  // A sortBy naming a member every object has (`constructor`, `valueOf`,
  // `__proto__`, ...) is dropped: the table looks column ids up on a plain
  // object, finds that member instead of a column, and crashes the page.
  const rawSortBy = searchParams.get('sortBy') || undefined;
  const urlSortBy = rawSortBy && !(rawSortBy in Object.prototype) ? rawSortBy : undefined;
  const urlSortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || undefined;

  // Derive DataTable SortingState from URL. Memoized on the resolved column id —
  // a string — not on the map: callers fetch in an effect keyed on `sorting`, and
  // a `sorting` that followed the map's identity refetched in a loop the moment
  // a caller wrote the map as an inline literal.
  const sortColumnId = urlSortBy ? (sortFieldToColumn.get(urlSortBy) ?? urlSortBy) : undefined;
  const sorting: SortingState = useMemo(() => {
    if (!sortColumnId) return [];
    return [{ id: sortColumnId, desc: urlSortOrder === 'desc' }];
  }, [sortColumnId, urlSortOrder]);

  const onSortingChange = useCallback(
    (newSorting: SortingState) => {
      const { columnToSortField } = latest.current;
      const newSortBy = newSorting.length > 0
        ? (Object.prototype.hasOwnProperty.call(columnToSortField, newSorting[0].id) ? columnToSortField[newSorting[0].id] : newSorting[0].id)
        : undefined;
      const newSortOrder = newSorting.length > 0 ? (newSorting[0].desc ? 'desc' : 'asc') : undefined;
      // Skip if sorting hasn't actually changed (avoids duplicate history entries
      // from TanStack table re-firing onSortingChange when controlled state syncs).
      // Checked here rather than left to `write`, because a change resets the page
      // and an unchanged sort must not.
      const url = current();
      if (newSortBy === (url.get('sortBy') || undefined) && newSortOrder === (url.get('sortOrder') || undefined)) return;

      write((next) => {
        if (newSortBy) {
          next.set('sortBy', newSortBy);
          next.set('sortOrder', newSortOrder!);
        } else {
          next.delete('sortBy');
          next.delete('sortOrder');
        }
        next.delete('page');
      });
    },
    [current, write],
  );

  const onSearchChange = useCallback(
    (value: string) => {
      // Skip if the search hasn't actually changed. SearchInput's debounce fires
      // for text typed and erased again, and Clear fires on an empty box; writing
      // then would add a history entry and throw the reader back to page 1.
      const previous = current().get('search') || '';
      if (value === previous) return;

      // Starting a search, or clearing one, is a step Back should undo. Editing a
      // search is not: the debounce fires at every pause in typing, and pushing
      // each one left a trail of half-typed queries to step back through. But an
      // edit may only replace the search's own entry. After a page click the
      // current entry is the pager's, and replacing that one put page 3 beyond
      // the reach of Back.
      const editing = previous !== '' && value !== '' && currentKind() === 'search';
      write((next) => {
        if (value) {
          next.set('search', value);
        } else {
          next.delete('search');
        }
        next.delete('page');
      }, { replace: editing, kind: 'search' });
    },
    [current, currentKind, write],
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      // Compare page numbers: ?page=1, ?page=0 and ?page=abc all read as page 1,
      // and clicking page 1 on any of them is not a change worth a history entry.
      if (newPage === readPage(current())) return;

      write((next) => {
        if (newPage > 1) {
          next.set('page', String(newPage));
        } else {
          next.delete('page');
        }
      });
    },
    [current, write],
  );

  const clampPage = useCallback(
    (pageCount: number) => {
      // A count that is not a number (a meta shape without one) is not a reason
      // to move the reader: `Math.max(1, NaN)` is NaN, and every comparison with
      // it fails the same way, which used to fall through to "go to page 1".
      if (!Number.isFinite(pageCount)) return;
      const last = Math.max(1, pageCount);
      if (readPage(current()) <= last) return;

      write((next) => {
        if (last > 1) {
          next.set('page', String(last));
        } else {
          next.delete('page');
        }
      }, { replace: true });
    },
    [current, write],
  );

  const setParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      const url = current();
      // Skip if every value is already in place — re-applying the same filter is
      // not a change, and must not reset the page or add a history entry.
      if (Object.entries(updates).every(([key, value]) => url.get(key) === (value || null))) return;

      write((next) => {
        for (const [key, value] of Object.entries(updates)) {
          if (!value) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        next.delete('page');
      });
    },
    [current, write],
  );

  return {
    search,
    page,
    limit: defaultLimit,
    sortBy: urlSortBy,
    sortOrder: urlSortOrder,
    sorting,
    onSortingChange,
    onSearchChange,
    onPageChange,
    clampPage,
    setParams,
  };
}

export interface ClientPage<T> {
  /** The rows on the page shown. */
  rows: T[];
  /** The page shown: the URL's, or the last page when the URL points past it. */
  currentPage: number;
  /** How many pages `items` make; never below 1. */
  pageCount: number;
}

/**
 * One page of a list held in memory, for a table that pages client-side with
 * `useDataTableUrl` (`Pagination` beside the table, DataTable's own paging off).
 *
 * `page` comes from the address bar, so a stale link — or removing the last row
 * on the last page — can point past the end. This shows the last real page
 * rather than an empty table that reads as "nothing here", and once the list
 * has loaded, corrects the URL to match so it can't jump back when the list
 * grows. `loaded` is false while the first fetch runs and after a failed one:
 * an empty list then must not read as "one page" and move the reader off theirs.
 */
export function useClientPage<T>(
  table: Pick<DataTableUrlResult, 'page' | 'limit' | 'clampPage'>,
  items: T[],
  loaded: boolean,
): ClientPage<T> {
  const { page, limit, clampPage } = table;
  const pageCount = Math.max(1, Math.ceil(items.length / limit));
  const currentPage = Math.min(page, pageCount);
  useEffect(() => {
    if (loaded) clampPage(pageCount);
  }, [loaded, page, pageCount, clampPage]);

  const rows = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return items.slice(start, start + limit);
  }, [items, currentPage, limit]);

  return { rows, currentPage, pageCount };
}
